import { t } from '../i18n.js';
import * as router from '../router.js';
import { fetchTip, isDismissed, dismissTip } from '../tip-service.js';

/**
 * Header food-tip strip: a single full-width tinted strip that sits between the
 * header and <main> on every breakpoint. The tip sentence wraps to as many
 * lines as it needs (no truncation) so the full text is always readable on both
 * mobile and desktop. Icon + label + optional open-arrow + dismiss.
 *
 * Replaces the earlier desktop-pill + mobile-banner pair, which truncated the
 * tip onto a single clipped line (see the 2026-07-07 wrapping-strip design).
 *
 * @param {{ columnClasses?: string }} [opts] — layout.js passes its shared
 *   content-column classes so the strip's inner content aligns with <main>.
 * @returns {{ el: HTMLElement }}
 */
export function tipStrip({ columnClasses = '' } = {}) {
  // Full-bleed tinted strip; inner content is constrained to the page content
  // column (columnClasses, shared with <main> via layout.js) so the text lines
  // up with the content below instead of stretching edge-to-edge on wide
  // monitors.
  const el = document.createElement('div');
  el.dataset.role = 'tip-strip';
  el.className = [
    'bg-primary-50 dark:bg-primary-900/40',
    'text-ink dark:text-ink-on-dark text-sm',
    'border-b border-border dark:border-border-dark',
  ].join(' ');

  const inner = document.createElement('div');
  inner.className = `${columnClasses} py-2 flex items-start gap-2`.trim();
  el.append(inner);

  // Tappable region: icon + wrapping label + open-arrow.
  const tipButton = document.createElement('button');
  tipButton.type = 'button';
  tipButton.dataset.role = 'tip-text-button';
  tipButton.className = 'flex-1 min-w-0 flex items-start gap-2 text-left';

  const tipText = document.createElement('span');
  tipText.dataset.role = 'tip-text';
  // Wraps freely — deliberately NO `truncate`; the whole point of this strip.
  tipText.className = 'flex-1 min-w-0';

  const tipArrow = document.createElement('span');
  tipArrow.setAttribute('aria-hidden', 'true');
  tipArrow.className = 'shrink-0';
  tipArrow.textContent = '→';

  tipButton.append(tipText, tipArrow);

  const dismissBtn = document.createElement('button');
  dismissBtn.type = 'button';
  dismissBtn.dataset.role = 'tip-dismiss';
  dismissBtn.className = 'btn shrink-0 !px-2';
  dismissBtn.textContent = '×';
  dismissBtn.setAttribute('aria-label', t('tips.dismiss'));

  inner.append(tipButton, dismissBtn);

  /** @type {object|null} */
  let currentTip = null;

  function render(tip) {
    currentTip = tip;

    // A payload without an i18n_key (stale cache from an older schema, or a
    // malformed response) would render "undefined" — treat it as no tip.
    if (!tip || !tip.i18n_key || isDismissed()) {
      el.style.display = 'none';
      return;
    }

    const label = t(tip.i18n_key, tip.params || {});
    const fullText = `${tip.icon || ''} ${label}`.trim();
    // textContent only — params carry server-resolved product/recipe names and
    // must never be parsed as markup (XSS-safety).
    tipText.textContent = fullText;

    // The → is a "tap to open" affordance; only show it when the tip actually
    // navigates. Curated tips carry no deep_link, so the surface is inert and
    // the arrow would be misleading (looks tappable, does nothing).
    const navigable = Boolean(tip.deep_link);
    tipArrow.hidden = !navigable;
    tipButton.disabled = !navigable;
    tipButton.classList.toggle('cursor-default', !navigable);

    el.style.display = '';
  }

  tipButton.addEventListener('click', () => {
    if (currentTip && currentTip.deep_link) router.go(currentTip.deep_link);
  });

  dismissBtn.addEventListener('click', (ev) => {
    ev.stopPropagation();
    dismissTip();
    el.style.display = 'none';
  });

  el.style.display = 'none';
  fetchTip().then(render);

  return { el };
}
